import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Check, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { getWalkinPublic, submitWalkin, submitWalkinMeasurements } from "@/lib/api";
import { emailWarning } from "@/lib/emailCheck";

// Public pre-sale questionnaire (no login): the "Welcome to LIFTR" sheet on the member's phone.
// Everything is tap-to-answer. It warms up with easy picks, then moves to first-person
// statements ("I avoid photos and mirrors"), so choosing one is saying the pain out loud.

const STEPS = ["About you", "Your goals", "Your history", "What got in the way", "Six weeks from now", "Why now", "How you feel", "Commitment"];

const Choice = ({ on, children, onClick }) => (
  <button type="button" onClick={onClick} aria-pressed={on}
    className={`w-full rounded-xl border px-4 py-3 text-left text-[15px] leading-snug transition ${on ? "border-rose-500 bg-rose-500/15 text-white" : "border-[#2a2a2a] bg-[#111] text-zinc-200"}`}>
    <span className="flex items-start gap-2">
      <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border flex items-center justify-center ${on ? "border-rose-400 bg-rose-500" : "border-zinc-600"}`}>
        {on && <Check className="h-3 w-3 text-white" />}
      </span>
      <span>{children}</span>
    </span>
  </button>
);

const Q = ({ label, hint, children }) => (
  <fieldset className="space-y-2.5">
    <legend className="text-base font-semibold text-zinc-100">{label}</legend>
    {hint && <p className="text-sm text-zinc-500 -mt-1">{hint}</p>}
    {children}
  </fieldset>
);

const input = "w-full rounded-xl bg-[#111] border border-[#2a2a2a] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:border-rose-500 focus:outline-none";

// Date of birth typed as DD MM YYYY (spaces go in as you type), or picked with the phone's own date wheel
// from the calendar button. Emits "YYYY-MM-DD" (what the API stores), or "" until it's a real date.
const pad2 = (n) => String(n).padStart(2, "0");
const isoToDob = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? `${iso.slice(8, 10)} ${iso.slice(5, 7)} ${iso.slice(0, 4)}` : "");
const dobToIso = (text) => {
  const m = (text || "").match(/^(\d{2}) (\d{2}) (\d{4})$/);
  if (!m) return "";
  const [d, mo, y] = [+m[1], +m[2], +m[3]];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d || dt > new Date()) return "";
  return `${y}-${pad2(mo)}-${pad2(d)}`;
};
function DobInput({ value, onChange }) {
  const [text, setText] = useState(isoToDob(value));
  const native = useRef(null);
  const type = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const t = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join(" ");
    setText(t);
    onChange(dobToIso(t));
  };
  const openPicker = () => {
    const el = native.current;
    if (!el) return;
    if (el.showPicker) { try { el.showPicker(); return; } catch { /* fall through */ } }
    el.focus(); el.click();
  };
  return (
    <div className="relative flex gap-2">
      <input className={input} value={text} onChange={(e) => type(e.target.value)} inputMode="numeric" autoComplete="bday"
        placeholder="DD MM YYYY" maxLength={10} aria-label="Date of birth, day month year" data-testid="walkin-dob" />
      <button type="button" onClick={openPicker} aria-label="Pick from a calendar" data-testid="walkin-dob-calendar"
        className="shrink-0 w-14 rounded-xl bg-[#111] border border-[#2a2a2a] flex items-center justify-center text-zinc-300 hover:border-rose-500">
        <CalendarDays className="h-5 w-5" />
      </button>
      <input ref={native} type="date" tabIndex={-1} aria-hidden="true" value={value || ""}
        max={new Date().toISOString().slice(0, 10)} min="1920-01-01"
        onChange={(e) => { setText(isoToDob(e.target.value)); onChange(e.target.value); }}
        className="absolute right-0 bottom-0 h-px w-px opacity-0 pointer-events-none" />
    </div>
  );
}

// After the questions: the member brings their phone to a coach at the front desk, who takes
// their weight and waist and saves them here (CR-0022). The coach can also skip and add them in FPA.
function MeasureStep({ done, slug, m, setM, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    const w = parseFloat(m.weight), c = parseFloat(m.waist);
    if (!(w > 0) || !(c > 0)) { setErr("Enter both the weight (kg) and the waist (cm)."); return; }
    setBusy(true); setErr("");
    try {
      await submitWalkinMeasurements(slug, { form_id: done.form_id, token: done.measure_token, weight_kg: w, waist_cm: c });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Couldn't save. Check the connection and try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 space-y-5" data-testid="walkin-measure">
      <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center"><Check className="h-8 w-8 text-emerald-400" /></div>
      <h1 className="font-display text-3xl font-extrabold uppercase">Almost done, {done.first_name}!</h1>
      <p className="text-lg text-zinc-200">Please take your phone to a coach at the front desk. They&apos;ll take your weight and waist measurement.</p>
      <div className="rounded-2xl border border-[#2a2a2a] bg-[#111] p-4 space-y-4">
        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Coach: enter and save</div>
        <label className="block space-y-1.5">
          <span className="text-sm text-zinc-300">Weight (kg)</span>
          <input type="number" inputMode="decimal" step="0.1" value={m.weight} onChange={(e) => setM((x) => ({ ...x, weight: e.target.value }))}
            className={input} placeholder="e.g. 78.5" data-testid="walkin-weight" />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm text-zinc-300">Waist (cm)</span>
          <input type="number" inputMode="decimal" step="0.5" value={m.waist} onChange={(e) => setM((x) => ({ ...x, waist: e.target.value }))}
            className={input} placeholder="e.g. 92" data-testid="walkin-waist" />
        </label>
        {err && <p className="text-sm text-amber-300" role="alert">{err}</p>}
        <button type="button" onClick={save} disabled={busy} data-testid="walkin-measure-save"
          className="h-12 w-full rounded-full bg-rose-600 text-white text-base font-semibold disabled:opacity-60">
          {busy ? "Saving…" : "Submit"}
        </button>
      </div>
      <button type="button" onClick={onDone} className="text-xs text-zinc-500 underline" data-testid="walkin-measure-skip">
        Coach: skip for now (add them in FPA)
      </button>
    </div>
  );
}

export default function WalkinForm() {
  const { slug } = useParams();
  // Prospects see LIFTR only: in-app browsers show the page title at the top.
  useEffect(() => { const before = document.title; document.title = "Welcome to LIFTR"; return () => { document.title = before; }; }, []);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(-1);            // -1 welcome, 0..7 questions, 8 measurements at the desk, 9 done
  const [m, setM] = useState({ weight: "", waist: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // After their details: the member sees them big and confirms (a typo means no bill or reminders).
  const [checking, setChecking] = useState(false);
  const [a, setA] = useState({
    name: "", phone: "", email: "", dob: "", events_soon: null, events_when: "",
    goals: [], goal_other: "", tried_before: null, tried: [], tried_text: "", restarts: "", since_best: "",
    stopped: [], stopped_other: "", worth_it: [], long_term: "", why: [], why_top: "", feelings: [], worry: "",
    commitment: null, note: "", recording_consent: null, water: null, consent: false, website: "",
  });
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const toggle = (k, v) => setA((x) => {
    const list = x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v];
    // Un-ticking the "hits hardest" pain clears it; with one pain ticked, that one is the answer.
    if (k !== "why") return { ...x, [k]: list };
    return { ...x, why: list, why_top: list.includes(x.why_top) && list.length > 1 ? x.why_top : "" };
  });

  useEffect(() => {
    getWalkinPublic(slug).then(setInfo).catch((e) => setErr(e?.response?.data?.detail || "This form link isn't working. Ask a coach for help."));
  }, [slug]);

  // The water question is India only for now (Sri Lanka and Dubai staff still mark it).
  const askWater = info?.country === "India";
  const problem = () => {
    if (step === 0) {
      if (a.name.trim().split(/\s+/).length < 2) return "Please enter your first and last name.";
      if (a.phone.replace(/\D/g, "").length < 7) return "Please enter your phone number.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email.trim())) return "Please enter your email address.";
      if (!a.dob) return "Please enter your date of birth as DD MM YYYY, or tap the calendar.";
    }
    if (step === 1) {
      if (!a.goals.length && !a.goal_other.trim()) return "Tap at least one goal.";
      if (a.events_soon === null) return "Any big events in the next 6 weeks? Tap yes or no.";
    }
    if (step === 2) {
      if (a.tried_before === null) return "Have you tried before? Tap yes or no.";
      if (a.tried_before && !a.restarts) return "How many times have you started and stopped?";
      if (!a.since_best) return "When did you last feel at your best?";
    }
    if (step === 3 && !a.stopped.length && !a.stopped_other.trim()) return "Tap at least one.";
    if (step === 4 && !a.worth_it.length) return "Tap at least one result.";
    if (step === 5) {
      if (!a.why.length) return "Tap the ones that are true for you.";
      if (a.why.length > 1 && !a.why_top) return "Which one hits hardest? Tap one.";
    }
    if (step === 6) {
      if (!a.feelings.length) return "Tap at least one.";
      if (!a.worry) return "Tap what worries you most.";
    }
    if (step === 7) {
      if (!a.commitment) return "Tap a number from 1 to 10.";
      if (a.recording_consent === null) return "About recording: tap \"Sure, that works.\" or \"No, please!\"";
      if (askWater && a.water === null) return "Did you get a bottle of water? Tap one.";
      if (!a.consent) return "Please tick the box at the bottom.";
    }
    return "";
  };
  const go = (n) => { setErr(""); setChecking(false); setStep(n); window.scrollTo(0, 0); };
  const next = async () => {
    const p = problem();
    if (p) { setErr(p); return; }
    if (step === 0 && !checking) { setErr(""); setChecking(true); window.scrollTo(0, 0); return; }
    if (step < 7) { go(step + 1); return; }
    setBusy(true);
    try { setDone(await submitWalkin(slug, a)); go(8); }   // then weight and waist at the front desk
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't send. Check your connection and try again."); }
    finally { setBusy(false); }
  };

  if (!info) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-zinc-200 flex items-center justify-center p-6 text-center">
        {err ? <p>{err}</p> : <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />}
      </div>
    );
  }
  const o = info.options;
  const pick = (k, many = true) => o[k].map((v) => (
    <Choice key={v} on={many ? a[k].includes(v) : a[k] === v} onClick={() => (many ? toggle(k, v) : set(k, v))}>{v}</Choice>
  ));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="walkin-form">
      <div className="mx-auto max-w-lg px-5 pb-36" style={{ paddingTop: "max(env(safe-area-inset-top), 24px)" }}>
        <div className="flex items-center justify-between"><Brand /><span className="text-xs text-zinc-500">{info.gym}</span></div>

        {step === -1 && (
          <div className="mt-12 space-y-5">
            <h1 className="font-display text-4xl font-extrabold uppercase leading-none">Welcome to LIFTR</h1>
            <p className="text-lg font-semibold text-zinc-200">Let's build a plan that actually works.</p>
            <p className="text-zinc-400">Just tap your answers. No long typing. It takes about 3 minutes, and it helps your coach understand what you really need.</p>
          </div>
        )}

        {step >= 0 && step <= 7 && (
          <div className="mt-6">
            <div className="flex gap-1" aria-hidden="true">
              {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-rose-500" : "bg-[#222]"}`} />)}
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.18em] text-zinc-500">Step {step + 1} of 8 · {STEPS[step]}</p>
          </div>
        )}

        <div className="mt-6 space-y-8">
          {step === 0 && checking && (
            <div className="space-y-5" data-testid="walkin-check-details">
              <h2 className="text-2xl font-bold">Is this right?</h2>
              <p className="text-zinc-400">Your bills and messages go here, so please check each letter.</p>
              {[["Name", a.name], ["Phone", a.phone], ["Email", a.email]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{k}</div>
                  <div className={`text-xl text-zinc-50 break-all ${k === "Name" ? "" : "font-mono"}`}>{v.trim()}</div>
                </div>
              ))}
              {emailWarning(a.email, a.name) && (() => {
                const w = emailWarning(a.email, a.name);
                return (
                  <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-100 space-y-2" data-testid="walkin-email-warning">
                    <p>{w.message}</p>
                    {w.suggestion && <button type="button" className="underline break-all text-left" onClick={() => set("email", w.suggestion)}>Use {w.suggestion}</button>}
                  </div>
                );
              })()}
              <button type="button" onClick={() => setChecking(false)} className="text-rose-300 underline">Change something</button>
            </div>
          )}

          {step === 0 && !checking && (<>
            <Q label="Your full name"><input className={input} value={a.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" placeholder="First and last name" /></Q>
            <Q label="Phone"><input className={input} value={a.phone} onChange={(e) => set("phone", e.target.value)} type="tel" autoComplete="tel" inputMode="tel" /></Q>
            <Q label="Email"><input className={input} value={a.email} onChange={(e) => set("email", e.target.value)} type="email" autoComplete="email" inputMode="email" /></Q>
            <Q label="Date of birth" hint="So we can send you birthday wishes and a birthday treat"><DobInput value={a.dob} onChange={(v) => set("dob", v)} /></Q>
            <input tabIndex={-1} autoComplete="off" className="hidden" value={a.website} onChange={(e) => set("website", e.target.value)} aria-hidden="true" />
          </>)}

          {step === 1 && (<>
            <Q label="What would you most like to achieve?" hint="Tap all that apply">
              <div className="grid grid-cols-2 gap-2">{pick("goals")}</div>
              <input className={input} value={a.goal_other} onChange={(e) => set("goal_other", e.target.value)} placeholder="Something else? (optional)" />
            </Q>
            <Q label="Any holidays, work trips or big events in the next 6 weeks?">
              <div className="grid grid-cols-2 gap-2">
                <Choice on={a.events_soon === false} onClick={() => set("events_soon", false)}>No</Choice>
                <Choice on={a.events_soon === true} onClick={() => set("events_soon", true)}>Yes</Choice>
              </div>
              {a.events_soon && <input className={input} value={a.events_when} onChange={(e) => set("events_when", e.target.value)} placeholder="What and when? (optional)" />}
            </Q>
          </>)}

          {step === 2 && (<>
            <Q label="Have you seriously tried to reach these goals before?">
              <div className="grid grid-cols-2 gap-2">
                <Choice on={a.tried_before === true} onClick={() => set("tried_before", true)}>Yes</Choice>
                <Choice on={a.tried_before === false} onClick={() => setA((x) => ({ ...x, tried_before: false, tried: [], restarts: "" }))}>No</Choice>
              </div>
            </Q>
            {a.tried_before && (<>
              <Q label="What have you tried?" hint="Tap all that apply"><div className="grid grid-cols-2 gap-2">{pick("tried")}</div></Q>
              <Q label="How many times have you started and stopped?">{pick("restarts", false)}</Q>
            </>)}
            <Q label="When did you last feel truly at your best?">{pick("since_best", false)}</Q>
          </>)}

          {step === 3 && (
            <Q label={a.tried_before ? "What got in the way last time?" : "What do you think has held you back so far?"} hint="Tap everything that's true">
              {pick("stopped")}
              <input className={input} value={a.stopped_other} onChange={(e) => set("stopped_other", e.target.value)} placeholder="Something else? (optional)" />
            </Q>
          )}

          {step === 4 && (<>
            <Q label="If we met again 6 weeks from today, what would make you say “this was worth it”?" hint="Tap all that apply">{pick("worth_it")}</Q>
            <Q label="And long term, what matters most?">{pick("long_term", false)}</Q>
          </>)}

          {step === 5 && (<>
            <Q label="Why does this matter to you now?" hint="Tap every statement that's true for you. Be honest, it stays between you and your coach.">{pick("why")}</Q>
            {a.why.length > 1 && (
              <Q label="Of those, which one hits hardest?">
                {a.why.map((v) => <Choice key={v} on={a.why_top === v} onClick={() => set("why_top", v)}>{v}</Choice>)}
              </Q>
            )}
          </>)}

          {step === 6 && (<>
            <Q label="How do you feel about where you are right now?" hint="Tap all that apply"><div className="grid grid-cols-2 gap-2">{pick("feelings")}</div></Q>
            <Q label="If nothing changes in the next year, what worries you most?">{pick("worry", false)}</Q>
          </>)}

          {step === 7 && (<>
            <Q label="On a scale of 1–10, how committed are you to making a change today?">
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button key={n} type="button" onClick={() => set("commitment", n)} aria-pressed={a.commitment === n}
                    className={`h-14 rounded-xl border text-xl font-bold ${a.commitment === n ? "border-rose-500 bg-rose-500 text-white" : "border-[#2a2a2a] bg-[#111] text-zinc-200"}`}>{n}</button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-zinc-500"><span>Just looking</span><span>Ready to start</span></div>
            </Q>
            <Q label="Anything else your coach should know?" hint="Optional, one line is plenty">
              <input className={input} value={a.note} onChange={(e) => set("note", e.target.value)} maxLength={300} placeholder="e.g. a knee injury, night shifts" />
            </Q>
            <Q label="We record our conversations for training and quality, to make sure our team gives everyone the best outcome for their training history and goals. The recording stays inside LIFTR and is never shared. Your consult is the same either way :)">
              <div className="grid grid-cols-2 gap-2" data-testid="walkin-recording-consent">
                <Choice on={a.recording_consent === true} onClick={() => set("recording_consent", true)}>Sure, that works.</Choice>
                <Choice on={a.recording_consent === false} onClick={() => set("recording_consent", false)}>No, please!</Choice>
              </div>
            </Q>
            {askWater && <Q label="Hey, just a quick check 💧 Did our team give you (or offer you) a bottle of water?">
              <div className="grid grid-cols-2 gap-2" data-testid="walkin-water">
                <Choice on={a.water === true} onClick={() => set("water", true)}>Yes, all hydrated</Choice>
                <Choice on={a.water === false} onClick={() => set("water", false)}>Nope, still thirsty</Choice>
              </div>
            </Q>}
            <label className="flex items-start gap-3 text-sm text-zinc-300">
              <input type="checkbox" className="mt-1 h-5 w-5" checked={a.consent} onChange={(e) => set("consent", e.target.checked)} />
              <span>LIFTR can use my answers and contact details to plan my training and contact me about it.</span>
            </label>
          </>)}

          {step === 8 && done && (
            <MeasureStep done={done} slug={slug} m={m} setM={setM} onDone={() => go(9)} />
          )}

          {step === 9 && done && (
            <div className="mt-10 space-y-5" data-testid="walkin-done">
              <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center"><Check className="h-8 w-8 text-emerald-400" /></div>
              <h1 className="font-display text-3xl font-extrabold uppercase">Thanks, {done.first_name}!</h1>
              <p className="text-lg text-zinc-200">Please let one of our coaches know you're done.</p>
              <p className="text-zinc-500">Your coach already has your answers.</p>
            </div>
          )}
        </div>
      </div>

      {step <= 7 && (
        <div className="fixed inset-x-0 bottom-0 border-t border-[#1f1f1f] bg-[#0a0a0a]/95 backdrop-blur" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="mx-auto max-w-lg px-5 py-3 space-y-2">
            {err && <p className="text-sm text-amber-300" role="alert">{err}</p>}
            <div className="flex gap-2">
              {step >= 0 && <button type="button" onClick={() => (step === 0 && checking ? setChecking(false) : go(step - 1))} className="h-12 px-5 rounded-full border border-[#2a2a2a] text-zinc-300">Back</button>}
              <button type="button" onClick={step === -1 ? () => go(0) : next} disabled={busy} data-testid="walkin-next"
                className="h-12 flex-1 rounded-full bg-rose-600 text-white text-base font-semibold disabled:opacity-60">
                {busy ? "Sending…" : step === -1 ? "Start" : step === 7 ? "Submit" : step === 0 && checking ? "Yes, that's right" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
